import React, { type ComponentProps, type ReactNode } from 'react';

import { bind } from 'bind-event-listener';

import { cssMap } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { TriggerProps } from '@atlaskit/popup/types';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import type { PopupTriggerWithHover } from '@atlaskit/teams-app-internal-popup-adaptor/popup-trigger-with-hover';

const styles = cssMap({
	trigger: {
		all: 'unset',
		display: 'inline',
	},
});

type TeamProfileCardTriggerProps = {
	trigger?: ComponentProps<typeof PopupTriggerWithHover>['triggerMode'];
	triggerProps: TriggerProps;
	children: ReactNode;
};

/**
 * The wrapper always has a native keydown listener (for hover-only popups), which would
 * otherwise steal Enter from a nested <a> before it can navigate. Space is left alone.
 */
const delegateEnterKeyToInnerLink = (e: React.KeyboardEvent<HTMLSpanElement>) => {
	if (e.key !== 'Enter') {
		return;
	}

	const link = e.currentTarget.querySelector<HTMLAnchorElement>('a[href]');
	if (!link) {
		// No link to delegate to: let the event bubble so the popup can still open on Enter.
		return;
	}

	e.preventDefault();
	// .click() (not dispatchEvent) is required for real navigation. Stop its propagation in the
	// bubble phase only, so it doesn't also trigger the wrapper's own popup-open handler.
	bind(link, {
		type: 'click',
		listener: (clickEvent) => clickEvent.stopPropagation(),
		options: { once: true },
	});
	link.click();
	e.stopPropagation();
};

export const TeamProfileCardTrigger = ({
	trigger,
	triggerProps,
	children,
}: TeamProfileCardTriggerProps): React.JSX.Element => {
	const isNonInteractiveHoverTriggerEnabled = fg('townsquare_a11y_team_profilecard');

	const hoverOnlyTrigger = Array.isArray(trigger)
		? trigger.every((mode) => mode === 'hover')
		: trigger === 'hover';
	const useNonInteractiveHoverTrigger = isNonInteractiveHoverTriggerEnabled && hoverOnlyTrigger;

	if (useNonInteractiveHoverTrigger) {
		const { 'aria-expanded': _ariaExpanded, ...nonDialogTriggerProps } = triggerProps;

		return (
			<Box
				as="span"
				{...nonDialogTriggerProps}
				onKeyDownCapture={delegateEnterKeyToInnerLink}
				testId="team-profilecard-trigger-wrapper"
				xcss={styles.trigger}
			>
				{children}
			</Box>
		);
	}

	// this branch of fg can be cleaned up once ai-a11y-exp-41902-team-hover is fully rolled out
	if (fg('platform_create_work_item_modernization')) {
		// Strip popup ARIA: a plain <span> has no role, so these attributes would be invalid on it.
		const {
			'aria-expanded': _ariaExpanded,
			'aria-haspopup': _ariaHaspopup,
			'aria-controls': _ariaControls,
			...spanTriggerProps
		} = triggerProps;

		return (
			<Box
				as="span"
				{...spanTriggerProps}
				onKeyDownCapture={delegateEnterKeyToInnerLink}
				testId="team-profilecard-trigger-wrapper"
				xcss={styles.trigger}
			>
				{children}
			</Box>
		);
	}

	return (
		<Pressable {...triggerProps} testId="team-profilecard-trigger-wrapper" xcss={styles.trigger}>
			{children}
		</Pressable>
	);
};
